//DNB_FIX использовать свою компоненту select-permission
import { SelectPermissions } from '@affine/component/affine-select-permission';
import { UserAccessRole } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ChipsProps } from '../../ui/chips';
import { ConfirmModal } from '../../ui/modal';
import { AuthInput } from '../auth-components';
import { emailRegex } from '../auth-components/utils';
import * as styles from './styles.css';

export interface InviteModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onConfirm: (params: { emails: string[]; permission: UserAccessRole }) => void;
  isMutating: boolean;
  //DNB_FIX
  permissions: UserAccessRole[] | null;
}

const defaultPermision = UserAccessRole.Read;

export const InviteModal = ({
  open,
  setOpen,
  onConfirm,
  isMutating,
  //DNB_FIX
  permissions,
}: InviteModalProps) => {
  const t = useI18n();
  const [inviteEmail, setInviteEmail] = useState('');
  const [emailList, setEmailList] = useState<ChipsProps['listItems']>([]);
  const [permission, setPermission] = useState(defaultPermision);
  const [isValidEmail, setIsValidEmail] = useState(true);

  const handleAddEmailToList = useCallback(() => {
    if (!emailRegex.test(inviteEmail)) {
      setIsValidEmail(false);
      return;
    }
    setIsValidEmail(true);
    setEmailList(prev => [...prev, { id: inviteEmail, name: inviteEmail }]);
    setInviteEmail('');
  }, [inviteEmail]);

  const commaKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ',') {
        e.preventDefault();
        handleAddEmailToList();
      }
    },
    [handleAddEmailToList]
  );

  const handleConfirm = useCallback(() => {
    if (inviteEmail && !emailRegex.test(inviteEmail)) {
      setIsValidEmail(false);
      return;
    }

    const emails = [...emailList];
    if (inviteEmail) {
      emails.push({ id: inviteEmail, name: inviteEmail });
    }

    setIsValidEmail(true);

    onConfirm({
      emails: emails.map(email => email.name),
      permission,
    });
  }, [emailList, inviteEmail, onConfirm, permission]);

  const handleChangePermission = useCallback(
    (value: UserAccessRole) => {
      setPermission(value);
    },
    [setPermission]
  );

  // 2 если вводимое значение не email
  const confirmButtonDisabled = useMemo(() => {
    // дизейблить, если нет ни вводимого значения, ни уже введенных
    if (!inviteEmail && !emailList.length) return true;
    // дизейблить, если вводимое значение - не email
    return !!inviteEmail && !emailRegex.test(inviteEmail);
  }, [inviteEmail, emailList.length]);

  useEffect(() => {
    if (!open) {
      setInviteEmail('');
      setIsValidEmail(true);
      setPermission(defaultPermision);
    }
  }, [open]);

  return (
    <ConfirmModal
      open={open}
      onOpenChange={setOpen}
      title={t['Invite Members']()}
      description={t['Invite Members Message']()}
      cancelText={t['com.affine.inviteModal.button.cancel']()}
      contentOptions={{
        ['data-testid' as string]: 'invite-modal',
        style: {
          padding: '20px 26px',
        },
      }}
      confirmText={t['Invite']()}
      confirmButtonOptions={{
        loading: isMutating,
        disabled: confirmButtonDisabled,
        variant: 'primary',
        ['data-testid' as string]: 'confirm-enable-dnb-cloud-button',
      }}
      onConfirm={handleConfirm}
    >
      <div className={styles.inviteContent}>
        <AuthInput
          disabled={isMutating}
          placeholder={t['docnboard.invite.template-email.placeholder']()}
          value={inviteEmail}
          onChange={setInviteEmail}
          error={!isValidEmail}
          errorHint={
            isValidEmail ? '' : t['com.affine.auth.sign.email.error']()
          }
          listItems={emailList}
          setListItems={setEmailList}
          onEnter={handleAddEmailToList}
          onKeyDown={commaKeyDown}
          size="large"
          classNameRoot={styles.inviteAuthInput}
          classNameHint={styles.inviteHint}
          multiple
        />
        {/* //DNB_FIX использовать свою компоненту select-permission */}
        <SelectPermissions
          onChange={handleChangePermission}
          permissions={permissions}
        />
      </div>
    </ConfirmModal>
  );
};
